import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, UploadCloud, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SERVICE_OPTIONS = [
  "2D Floor Plan",
  "3D Floor Plan",
  "Renew Existing Floor Plan",
  "Other",
];

export const STYLE_OPTIONS = [
  "Black & White",
  "Colorized",
  "Black & White + Colorized",
  "Site Plan",
  "Black & White + Site Plan",
  "Colorized + Site Plan",
  "Redraw",
];

const MAX_FILE_BYTES = 3 * 1024 * 1024; // 3MB per file
const MAX_FILES = 5;
const ACCEPTED_FILE_TYPES = ".pdf,.png,.jpg,.jpeg,.dxf,.dwg,.heic,.webp";

/**
 * Reusable lead-submission form. Used both in the public Contact section
 * and inside the authenticated "+ New request" modal on the Account page.
 *
 * Props:
 *  - prefill: { name?, email? } — pre-populate form fields
 *  - authHeader: () => object — optional, attaches auth headers on submit
 *  - onSuccess: (lead) => void — invoked after a successful submission
 *  - showSuccessState: boolean — when false (modal), the parent owns the success UI
 *  - submitLabel: string — override the submit button text
 *  - footerNote: ReactNode — override the small footer note under the submit button
 */
export default function LeadForm({
  prefill = {},
  authHeader,
  onSuccess,
  showSuccessState = true,
  submitLabel = "Request a Quote",
  footerNote,
}) {
  const [form, setForm] = useState({
    name: prefill.name || "",
    email: prefill.email || "",
    phone: "",
    property_address: "",
    service_type: SERVICE_OPTIONS[0],
    style: STYLE_OPTIONS[0],
    message: "",
  });
  const [existingPlans, setExistingPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("read-failed"));
      reader.readAsDataURL(file);
    });

  const onFileChange = async (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = "";
    if (!incoming.length) return;
    const slotsLeft = MAX_FILES - existingPlans.length;
    if (slotsLeft <= 0) {
      toast.error(`You can attach up to ${MAX_FILES} files.`);
      return;
    }
    const toProcess = incoming.slice(0, slotsLeft);
    if (incoming.length > slotsLeft) {
      toast.message(`Only added ${slotsLeft} of ${incoming.length} — ${MAX_FILES}-file max.`);
    }
    const accepted = [];
    for (const file of toProcess) {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`"${file.name}" is over 3 MB — skipped.`);
        continue;
      }
      try {
        const base64 = await readAsDataUrl(file);
        accepted.push({ name: file.name, base64 });
      } catch {
        toast.error(`Could not read "${file.name}".`);
      }
    }
    if (accepted.length) setExistingPlans((prev) => [...prev, ...accepted]);
  };

  const removeFile = (idx) =>
    setExistingPlans((prev) => prev.filter((_, i) => i !== idx));
  const clearAllFiles = () => setExistingPlans([]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const showsStyle = ["2D Floor Plan", "Renew Existing Floor Plan"].includes(
        form.service_type
      );
      const payload = {
        ...form,
        style: showsStyle ? form.style : null,
        existing_plan_files: existingPlans.length
          ? existingPlans.map((p) => ({ filename: p.name, data: p.base64 }))
          : null,
      };
      const { data } = await axios.post(`${API}/leads`, payload, {
        headers: authHeader ? authHeader() : {},
        withCredentials: true,
      });
      setSubmitted(true);
      toast.success("Quote request received. We'll reach out within 24 hours.");
      setForm({
        name: prefill.name || "",
        email: prefill.email || "",
        phone: "",
        property_address: "",
        service_type: SERVICE_OPTIONS[0],
        style: STYLE_OPTIONS[0],
        message: "",
      });
      setExistingPlans([]);
      onSuccess?.(data);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && showSuccessState) {
    return <SuccessState onReset={() => setSubmitted(false)} />;
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full Name" required>
          <input
            data-testid="contact-form-name"
            type="text"
            value={form.name}
            onChange={onChange("name")}
            placeholder="Jane Doe"
            className="input-bubbly"
            required
          />
        </Field>
        <Field label="Email" required>
          <input
            data-testid="contact-form-email"
            type="email"
            value={form.email}
            onChange={onChange("email")}
            placeholder="jane@example.com"
            className="input-bubbly"
            required
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Phone">
          <input
            data-testid="contact-form-phone"
            type="tel"
            value={form.phone}
            onChange={onChange("phone")}
            placeholder="(202) 555-0184"
            className="input-bubbly"
          />
        </Field>
        <Field label="Service Type" required>
          <select
            data-testid="contact-form-service"
            value={form.service_type}
            onChange={onChange("service_type")}
            className="input-bubbly cursor-pointer"
          >
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {["2D Floor Plan", "Renew Existing Floor Plan"].includes(form.service_type) && (
        <Field label="Style">
          <select
            data-testid="contact-form-style"
            value={form.style}
            onChange={onChange("style")}
            className="input-bubbly cursor-pointer"
          >
            {STYLE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Property Address">
          <input
            data-testid="contact-form-address"
            type="text"
            value={form.property_address}
            onChange={onChange("property_address")}
            placeholder="123 K St NW, Washington, DC"
            className="input-bubbly"
          />
          <p className="mt-2 text-[11px] italic text-stone-500 pl-1">
            *email any and all large files to redraw@dmvfp.com*
          </p>
        </Field>
        <Field label="Project Details">
          <textarea
            data-testid="contact-form-message"
            value={form.message}
            onChange={onChange("message")}
            rows={4}
            placeholder="Sq footage, levels, deadlines, anything we should know…"
            className="input-bubbly resize-none"
          />
        </Field>
      </div>

      <Field label={`Existing Floor Plans (optional — up to ${MAX_FILES} for renewals)`}>
        <div className="space-y-3" data-testid="contact-form-files-wrap">
          {existingPlans.map((p, idx) => (
            <div
              key={`${p.name}-${idx}`}
              className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3"
              data-testid={`contact-form-file-${idx}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <UploadCloud className="h-4 w-4 text-teal-700" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-stone-900 truncate">{p.name}</div>
                  <div className="text-[11px] text-stone-500">
                    File {idx + 1} of {MAX_FILES} · ready to send
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(idx)}
                data-testid={`contact-form-file-remove-${idx}`}
                className="h-8 w-8 rounded-full hover:bg-teal-100 flex items-center justify-center text-stone-600"
                aria-label={`Remove ${p.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          {existingPlans.length < MAX_FILES && (
            <label
              htmlFor="existing-plan-files"
              className="flex items-center gap-3 bg-[#FAF7F2] border border-dashed border-stone-300 hover:border-teal-500 hover:bg-teal-50/50 rounded-2xl px-4 py-3 cursor-pointer transition-colors"
            >
              <div className="h-9 w-9 rounded-full bg-white border border-stone-200 flex items-center justify-center flex-shrink-0">
                <UploadCloud className="h-4 w-4 text-teal-700" />
              </div>
              <div className="text-sm">
                <span className="font-semibold text-stone-900">
                  {existingPlans.length === 0
                    ? "Upload your old plans"
                    : `Add another (${existingPlans.length}/${MAX_FILES})`}
                </span>
                <span className="text-stone-500"> — PDF, JPG, PNG, DXF (max 3MB each)</span>
              </div>
              <input
                id="existing-plan-files"
                data-testid="contact-form-file"
                type="file"
                multiple
                className="hidden"
                accept={ACCEPTED_FILE_TYPES}
                onChange={onFileChange}
              />
            </label>
          )}

          {existingPlans.length > 1 && (
            <button
              type="button"
              onClick={clearAllFiles}
              data-testid="contact-form-files-clear-all"
              className="text-xs font-semibold text-stone-500 hover:text-stone-900 underline-offset-2 hover:underline"
            >
              Clear all files
            </button>
          )}
        </div>
      </Field>

      <p
        className="text-xs text-stone-500 italic text-center -mb-2"
        data-testid="contact-rush-fee-note"
      >
        *requests needed within 48 hours of contact occur a 50% rush fee*
      </p>

      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit-btn"
        className="group w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-white py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            {submitLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>

      {footerNote !== undefined ? (
        footerNote
      ) : (
        <p className="text-xs text-stone-500 text-center">
          We reply within 1 business day · No spam, ever
        </p>
      )}

      <style>{`
        .input-bubbly {
          width: 100%;
          background: #FAF7F2;
          border: 1px solid #E7E5E4;
          padding: 0.85rem 1.1rem;
          border-radius: 9999px;
          font-size: 0.95rem;
          color: #1c1917;
          font-family: 'Open Sans', sans-serif;
          transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
          outline: none;
        }
        textarea.input-bubbly { border-radius: 1.5rem; }
        .input-bubbly::placeholder { color: #a8a29e; }
        .input-bubbly:focus {
          border-color: #0F766E;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(15,118,110,0.12);
        }
      `}</style>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-600 mb-2 block pl-1">
        {label} {required && <span className="text-teal-700">*</span>}
      </span>
      {children}
    </label>
  );
}

function SuccessState({ onReset }) {
  return (
    <div className="h-full flex flex-col items-start justify-center py-12" data-testid="contact-success">
      <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
        <Sparkles className="h-3.5 w-3.5" />
        Confirmed · all set
      </div>
      <h3 className="font-display text-4xl font-semibold mt-6 text-stone-900 leading-tight">
        Quote request received.
      </h3>
      <p className="mt-4 text-stone-600 max-w-md leading-relaxed">
        Thanks for reaching out! A real human from the DMV Floor Plans team will
        reply within one business day with a transparent quote and next steps.
      </p>
      <button
        onClick={onReset}
        data-testid="contact-submit-another"
        className="mt-8 inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
      >
        Submit another request
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
